import React, { useEffect, useState, useRef } from 'react';
import { Receipt, Search, Filter, Download, Eye, CheckCircle2, Clock, XCircle, AlertTriangle, RefreshCw, Zap, Video, MoreVertical, MessageSquare, User, Ban, Undo2, FileText, CreditCard, History, X, ChevronDown, Timer, Hourglass } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Tabs } from '../ui/Tabs';
// --- Fixed-position Tooltip (escapes overflow containers) ---
function Tooltip({
  children,
  text
}: {
  children: React.ReactNode;
  text: string;
}) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  const ref = useRef<HTMLSpanElement>(null);
  const handleEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        top: rect.top - 4,
        left: rect.left + rect.width / 2
      });
    }
    setShow(true);
  };
  return <span ref={ref} className="relative inline-flex" onMouseEnter={handleEnter} onMouseLeave={() => setShow(false)} data-id="element-6118">
      {children}
      {show && <span className="fixed px-2.5 py-1.5 text-[10px] font-medium text-white bg-slate-800 rounded-lg whitespace-nowrap z-[9999] shadow-xl pointer-events-none -translate-x-1/2 -translate-y-full" style={{
      top: pos.top,
      left: pos.left
    }} data-id="element-6119">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" data-id="element-6120" />
        </span>}
    </span>;
}
interface AdminOrdersManagerProps {
  orders: any[];
  users: any[];
  astrologers: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  setCreatePanel?: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminOrdersManager({
  orders,
  users,
  astrologers,
  setActiveTab,
  setDetailPanel,
  setCreatePanel,
  success
}: AdminOrdersManagerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    payment: 'all',
    serviceType: 'all',
    astrologer: '',
    client: '',
    minAmount: '',
    maxAmount: '',
    date: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [activeOrderTab, setActiveOrderTab] = useState('main');
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState<string | null>(null);
  const [showRefundConfirm, setShowRefundConfirm] = useState<string | null>(null);
  // --- GENERATE EXTRA MOCK DATA for 1000+ scale ---
  const generateExtraOrders = () => {
    const services = ['Натальная карта', 'AI Натальная карта', 'Консультация онлайн', 'Курс Таро', 'Прогноз на год', 'AI Совместимость', 'Обучение астрологии', 'Консультация VIP', 'Солнечный возврат', 'AI Транзиты', 'Курс натальной астрологии', 'Консультация бизнес'];
    const clients = ['Анна К.', 'Михаил Д.', 'Елена С.', 'Дмитрий П.', 'Ольга В.', 'Сергей Н.', 'Мария Л.', 'Алексей Р.', 'Наталья Б.', 'Иван Т.', 'Татьяна М.', 'Павел Г.'];
    const astrologerNames = ['Мария Звёздная', 'Алексей Лунный', 'Ольга Солнцева', 'Павел Орион', null, null];
    const statuses = ['completed', 'in_progress', 'in_progress', 'completed', 'cancelled', 'in_progress'];
    const extra: any[] = [];
    for (let i = 0; i < 80; i++) {
      const service = services[i % services.length];
      const isAI = service.includes('AI');
      extra.push({
        id: `ORD-${9000 + i}`,
        service,
        client: clients[i % clients.length],
        astrologer: isAI ? null : astrologerNames[i % astrologerNames.length],
        status: statuses[i % statuses.length],
        amount: `${2000 + i * 500 % 15000} ₽`,
        price: `${2000 + i * 500 % 15000} ₽`,
        date: `${10 + i % 20}.03.2026`
      });
    }
    return extra;
  };
  const allOrders = orders.length < 30 ? [...orders, ...generateExtraOrders()] : orders;
  // --- MOCK DATA ENRICHMENT (now uses mutable state) ---
  const [localStatuses, setLocalStatuses] = useState<Record<string, {
    status: string;
    paymentStatus: string;
  }>>({});
  const enrichedOrders = allOrders.map((o, i) => {
    const isAI = o.service.includes('AI') || o.service.includes('Авто');
    const isConsultation = o.service.includes('Консультация');
    const isCourse = o.service.includes('Курс') || o.service.includes('Обучение');
    const type = isAI ? 'AI отчёт' : isConsultation ? 'Консультация' : isCourse ? 'Курс' : 'Услуга';
    const isOverdue = i % 7 === 0 && !isAI;
    const hasDispute = i % 12 === 0;
    const hasPaymentIssue = i % 15 === 0;
    const hasActiveChat = i % 5 === 0 && !isAI;
    const amountNum = parseInt(String(o.amount || o.price || '0').replace(/\D/g, '')) || 3900;
    const commissionRate = isAI ? 0.5 : 0.2;
    const commission = amountNum * commissionRate;
    let astrologerStatus = '—';
    if (!isAI) {
      if (o.status === 'completed') astrologerStatus = 'отправил результат';else if (i % 3 === 0) astrologerStatus = 'принял заказ';else if (i % 2 === 0) astrologerStatus = 'выполняет';else astrologerStatus = 'ожидает';
    }
    // Last action
    const lastActions = ['Клиент написал в чат', 'Астролог отправил результат', 'Оплата подтверждена', 'Астролог принял заказ', 'Клиент оставил отзыв', 'Загружен документ'];
    const lastAction = hasDispute ? 'Клиент открыл спор' : isAI ? 'AI отчёт сгенерирован' : lastActions[i % lastActions.length];
    const lastActionTime = `${(10 + i * 7 % 14).toString().padStart(2, '0')}:${(i * 13 % 60).toString().padStart(2, '0')}`;
    // Duration
    const durations = ['45 мин', '2 часа', '1 день', '3 дня', '5 дней', '12 часов'];
    const duration = isAI ? '< 1 мин' : durations[i % durations.length];
    // Chat messages mock
    const chatMessages = [{
      from: 'client',
      name: o.client,
      text: 'Здравствуйте! Хотела уточнить по натальной карте.',
      time: '14:10'
    }, {
      from: 'astrologer',
      name: o.astrologer || 'Астролог',
      text: 'Добрый день! Конечно, я уже начал работу над вашим заказом.',
      time: '14:15'
    }, {
      from: 'client',
      name: o.client,
      text: 'Спасибо! Когда примерно будет готово?',
      time: '14:20'
    }, {
      from: 'astrologer',
      name: o.astrologer || 'Астролог',
      text: 'Ориентировочно завтра к обеду отправлю результат.',
      time: '14:25'
    }, ...(hasDispute ? [{
      from: 'client',
      name: o.client,
      text: 'Я не доволен результатом, открываю спор.',
      time: '15:30'
    }] : [])];
    return {
      ...o,
      id: o.id || `ORD-${8000 + i}`,
      status: localStatuses[o.id || `ORD-${8000 + i}`]?.status || (hasDispute ? 'disputed' : o.status || (i % 4 === 0 ? 'completed' : 'in_progress')),
      paymentStatus: localStatuses[o.id || `ORD-${8000 + i}`]?.paymentStatus || (hasPaymentIssue ? 'failed' : 'paid'),
      orderType: type,
      isAI,
      astrologerStatus,
      dueDate: isAI ? 'Мгновенно' : isOverdue ? 'Просрочен на 1 дн' : i % 2 === 0 ? 'Через 2 дня' : 'Через 6 часов',
      isOverdue,
      hasDispute,
      hasPaymentIssue,
      hasActiveChat,
      amountNum,
      commission,
      commissionRate,
      lastAction,
      lastActionTime,
      duration,
      chatMessages,
      history: [{
        time: '12:10',
        text: 'Заказ создан',
        actor: 'Система'
      }, {
        time: '12:12',
        text: 'Оплата получена',
        actor: 'Система'
      }, ...(!isAI ? [{
        time: '12:14',
        text: 'Астролог принял заказ',
        actor: o.astrologer || 'Астролог'
      }] : [{
        time: '12:12',
        text: 'AI отчёт сгенерирован',
        actor: 'Система'
      }]), ...(o.status === 'completed' || i % 4 === 0 ? [{
        time: '14:40',
        text: 'Результат отправлен клиенту',
        actor: o.astrologer || 'Система'
      }] : []), ...(hasDispute ? [{
        time: '15:30',
        text: 'Клиент открыл спор',
        actor: o.client
      }] : []), ...(o.status === 'completed' ? [{
        time: '16:00',
        text: 'Заказ завершён',
        actor: 'Система'
      }] : [])]
    };
  });
  // KPI Calculations
  const totalOrders = enrichedOrders.length;
  const newOrders = enrichedOrders.filter(o => o.status === 'in_progress' && o.astrologerStatus === 'ожидает').length;
  const paidOrders = enrichedOrders.filter(o => o.paymentStatus === 'paid').length;
  const inProgress = enrichedOrders.filter(o => o.status === 'in_progress').length;
  const completed = enrichedOrders.filter(o => o.status === 'completed').length;
  const canceled = enrichedOrders.filter(o => o.status === 'cancelled').length;
  const disputed = enrichedOrders.filter(o => o.hasDispute).length;
  const refunds = enrichedOrders.filter(o => o.paymentStatus === 'refunded').length;
  const overdueCount = enrichedOrders.filter(o => o.isOverdue).length;
  const renderKpiCard = (label: string, value: string | number, icon: React.ReactNode, bgClass: string, textClass: string, filterKey: string, filterValue: string) => <Card padding="sm" hoverable className={`rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer group transition-all ${filterKey === 'status' && filters.status === filterValue || filterKey === 'payment' && filters.payment === filterValue || filterKey === 'dispute' && filterValue === 'true' && filters.status === 'disputed' ? 'ring-2 ring-indigo-500 bg-indigo-50/30' : ''}`} onClick={() => {
    if (filterKey === 'dispute') {
      setFilters({
        ...filters,
        status: 'disputed'
      });
    } else {
      setFilters({
        ...filters,
        [filterKey]: filterValue
      });
    }
  }} data-id="element-6121">
      <div className="flex justify-between items-start" data-id="element-6122">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-6123">
          {icon}
        </div>
      </div>
      <div data-id="element-6124">
        <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-6125">{label}</div>
        <div className="text-xl font-bold text-slate-900" data-id="element-6126">{value}</div>
      </div>
    </Card>;
  const handleExport = () => {
    success(`Экспорт ${filteredOrders.length} заказов завершён`);
  };
  // Single order actions
  const cancelOrder = (id: string) => {
    setLocalStatuses(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        status: 'cancelled',
        paymentStatus: prev[id]?.paymentStatus || 'paid'
      }
    }));
    setShowCancelConfirm(null);
    setOpenActionMenuId(null);
    success(`Заказ ${id} отменён`);
  };
  const refundOrder = (id: string) => {
    setLocalStatuses(prev => ({
      ...prev,
      [id]: {
        status: 'cancelled',
        paymentStatus: 'refunded'
      }
    }));
    setShowRefundConfirm(null);
    setOpenActionMenuId(null);
    success(`Возврат по заказу ${id} оформлен`);
  };
  // Bulk Actions
  const toggleAll = () => {
    if (selectedIds.size === currentOrders.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentOrders.map(o => o.id)));
    }
  };
  const toggleOne = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedIds(newSet);
  };
  const handleBulkCancel = () => {
    const updates: Record<string, {
      status: string;
      paymentStatus: string;
    }> = {};
    selectedIds.forEach(id => {
      updates[id] = {
        status: 'cancelled',
        paymentStatus: localStatuses[id]?.paymentStatus || 'paid'
      };
    });
    setLocalStatuses(prev => ({
      ...prev,
      ...updates
    }));
    success(`Отменено заказов: ${selectedIds.size}`);
    setSelectedIds(new Set());
  };
  const handleBulkRefund = () => {
    const updates: Record<string, {
      status: string;
      paymentStatus: string;
    }> = {};
    selectedIds.forEach(id => {
      updates[id] = {
        status: 'cancelled',
        paymentStatus: 'refunded'
      };
    });
    setLocalStatuses(prev => ({
      ...prev,
      ...updates
    }));
    success(`Оформлен возврат для ${selectedIds.size} заказов`);
    setSelectedIds(new Set());
  };
  // Filtering
  const filteredOrders = enrichedOrders.filter(o => {
    const matchesSearch = o.id.toLowerCase().includes(searchQuery.toLowerCase()) || o.client.toLowerCase().includes(searchQuery.toLowerCase()) || o.service.toLowerCase().includes(searchQuery.toLowerCase()) || o.astrologer && o.astrologer.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;
    if (filters.status !== 'all') {
      if (filters.status === 'new' && (o.status !== 'in_progress' || o.astrologerStatus !== 'ожидает')) return false;
      if (filters.status === 'overdue' && !o.isOverdue) return false;
      if (filters.status !== 'new' && filters.status !== 'overdue' && o.status !== filters.status) return false;
    }
    if (filters.payment !== 'all' && o.paymentStatus !== filters.payment) return false;
    if (filters.serviceType !== 'all' && o.orderType !== filters.serviceType) return false;
    if (filters.astrologer && (!o.astrologer || !o.astrologer.toLowerCase().includes(filters.astrologer.toLowerCase()))) return false;
    if (filters.client && !o.client.toLowerCase().includes(filters.client.toLowerCase())) return false;
    if (filters.minAmount && o.amountNum < parseInt(filters.minAmount)) return false;
    if (filters.maxAmount && o.amountNum > parseInt(filters.maxAmount)) return false;
    return true;
  });
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const currentOrders = filteredOrders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  useEffect(() => {
    const handleClickOutside = () => setOpenActionMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  return <div className="space-y-6 relative" data-id="element-6127">
      <div className="flex justify-between items-center" data-id="element-6128">
        <div data-id="element-6129">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-6130">Заказы</h2>
          <p className="text-sm text-slate-500" data-id="element-6131">
            Реестр всех заказов на платформе
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-id="element-6132">
        {renderKpiCard('Всего', totalOrders, <Receipt className="w-4 h-4 text-indigo-600" data-id="element-6133" />, 'bg-indigo-50', 'text-indigo-600', 'status', 'all')}
        {renderKpiCard('Новые', newOrders, <Zap className="w-4 h-4 text-blue-600" data-id="element-6134" />, 'bg-blue-50', 'text-blue-600', 'status', 'new')}
        {renderKpiCard('Оплаченные', paidOrders, <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-6135" />, 'bg-emerald-50', 'text-emerald-600', 'payment', 'paid')}
        {renderKpiCard('В работе', inProgress, <Clock className="w-4 h-4 text-amber-600" data-id="element-6136" />, 'bg-amber-50', 'text-amber-600', 'status', 'in_progress')}
        {renderKpiCard('Завершённые', completed, <CheckCircle2 className="w-4 h-4 text-emerald-600" data-id="element-6137" />, 'bg-emerald-50', 'text-emerald-600', 'status', 'completed')}
        {renderKpiCard('Отменённые', canceled, <XCircle className="w-4 h-4 text-slate-600" data-id="element-6138" />, 'bg-slate-50', 'text-slate-600', 'status', 'cancelled')}
        {renderKpiCard('Спорные', disputed, <AlertTriangle className="w-4 h-4 text-orange-600" data-id="element-6139" />, 'bg-orange-50', 'text-orange-600', 'dispute', 'true')}
        {renderKpiCard('Возвраты', refunds, <RefreshCw className="w-4 h-4 text-rose-600" data-id="element-6140" />, 'bg-rose-50', 'text-rose-600', 'payment', 'refunded')}
      </div>

      {/* Filter Bar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-6141">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-6142">
          <div className="flex items-center gap-3 w-full sm:w-auto flex-1" data-id="element-6143">
            <div className="relative flex-1 max-w-md" data-id="element-6144">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-6145" />
              <input type="text" placeholder="Поиск по ID заказа, клиенту, услуге..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6146" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-6148" />} onClick={() => setShowFilters(!showFilters)} data-id="element-6147">
              Фильтры
            </Button>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-6149">
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-6151" />} onClick={handleExport} data-id="element-6150">
              Экспорт CSV
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in fade-in slide-in-from-top-2" data-id="element-6152">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4" data-id="element-6153">
              <div data-id="element-6154">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6155">
                  Статус заказа
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-6156">
                  <option value="all" data-id="element-6157">Все</option>
                  <option value="new" data-id="element-6158">Новые</option>
                  <option value="in_progress" data-id="element-6159">В работе</option>
                  <option value="completed" data-id="element-6160">Завершённые</option>
                  <option value="disputed" data-id="element-6161">Спорные</option>
                  <option value="cancelled" data-id="element-6162">Отменённые</option>
                  <option value="overdue" data-id="element-6163">
                    ⏱ Просроченные ({overdueCount})
                  </option>
                </select>
              </div>
              <div data-id="element-6164">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6165">
                  Статус оплаты
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.payment} onChange={e => setFilters({
              ...filters,
              payment: e.target.value
            })} data-id="element-6166">
                  <option value="all" data-id="element-6167">Все</option>
                  <option value="paid" data-id="element-6168">Оплачен</option>
                  <option value="pending" data-id="element-6169">Ожидает</option>
                  <option value="failed" data-id="element-6170">Ошибка</option>
                  <option value="refunded" data-id="element-6171">Возврат</option>
                </select>
              </div>
              <div data-id="element-6172">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6173">
                  Тип услуги
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.serviceType} onChange={e => setFilters({
              ...filters,
              serviceType: e.target.value
            })} data-id="element-6174">
                  <option value="all" data-id="element-6175">Все</option>
                  <option value="AI отчёт" data-id="element-6176">AI отчёт</option>
                  <option value="Консультация" data-id="element-6177">Консультация</option>
                  <option value="Курс" data-id="element-6178">Курс</option>
                  <option value="Услуга" data-id="element-6179">Услуга</option>
                </select>
              </div>
              <div data-id="element-6180">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6181">
                  Сумма (₽)
                </label>
                <div className="flex gap-2" data-id="element-6182">
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="от" value={filters.minAmount} onChange={e => setFilters({
                ...filters,
                minAmount: e.target.value
              })} data-id="element-6183" />
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="до" value={filters.maxAmount} onChange={e => setFilters({
                ...filters,
                maxAmount: e.target.value
              })} data-id="element-6184" />
                </div>
              </div>
              <div data-id="element-6185">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6186">
                  Астролог
                </label>
                <input type="text" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="Имя астролога" value={filters.astrologer} onChange={e => setFilters({
              ...filters,
              astrologer: e.target.value
            })} data-id="element-6187" />
              </div>
              <div data-id="element-6188">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6189">
                  Клиент
                </label>
                <input type="text" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="Имя клиента" value={filters.client} onChange={e => setFilters({
              ...filters,
              client: e.target.value
            })} data-id="element-6190" />
              </div>
              <div className="flex items-end col-span-2" data-id="element-6191">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              payment: 'all',
              serviceType: 'all',
              astrologer: '',
              client: '',
              minAmount: '',
              maxAmount: '',
              date: 'all'
            })} data-id="element-6192">
                  Сбросить фильтры
                </Button>
              </div>
            </div>
          </div>}
      </Card>

      {/* Data Table - FIXED OVERFLOW */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-visible" data-id="element-6193">
        {/* Summary bar */}
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between" data-id="element-6194">
          <div className="flex items-center gap-4" data-id="element-6195">
            <span className="text-xs font-medium text-slate-500" data-id="element-6196">
              Показано{' '}
              {Math.min((currentPage - 1) * itemsPerPage + 1, filteredOrders.length)}
              –{Math.min(currentPage * itemsPerPage, filteredOrders.length)} из{' '}
              {filteredOrders.length}
            </span>
            {filteredOrders.length !== enrichedOrders.length && <span className="text-[10px] text-slate-400" data-id="element-6197">
                (всего {enrichedOrders.length})
              </span>}
          </div>
          <div className="flex items-center gap-4" data-id="element-6198">
            <span className="text-xs text-slate-500" data-id="element-6199">
              Общая сумма:{' '}
              <span className="font-bold text-slate-900" data-id="element-6200">
                {filteredOrders.reduce((sum, o) => sum + o.amountNum, 0).toLocaleString('ru-RU')}{' '}
                ₽
              </span>
            </span>
            <span className="text-xs text-slate-500" data-id="element-6201">
              Комиссия:{' '}
              <span className="font-bold text-emerald-600" data-id="element-6202">
                {filteredOrders.reduce((sum, o) => sum + o.commission, 0).toLocaleString('ru-RU')}{' '}
                ₽
              </span>
            </span>
          </div>
        </div>
        <div className="overflow-x-auto overflow-y-visible" data-id="element-6203">
          <table className="w-full text-left" style={{
          minWidth: '1000px'
        }} data-id="element-6204">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-6205">
              <tr data-id="element-6206">
                <th className="px-2 py-2.5 w-8" data-id="element-6207">
                  <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.size === currentOrders.length && currentOrders.length > 0} onChange={toggleAll} data-id="element-6208" />
                </th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6209">ID</th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6210">Тип / Услуга</th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6211">Клиент</th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6212">Астролог</th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6213">Сумма</th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6214">Срок / Длит.</th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6215">Последнее</th>
                <th className="px-2 py-2.5 font-medium" data-id="element-6216">Статус</th>
                <th className="px-2 py-2.5 text-right font-medium w-10" data-id="element-6217"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6218">
              {currentOrders.length === 0 ? <tr data-id="element-6219">
                  <td colSpan={10} className="px-3 py-8 text-center text-slate-500 text-sm" data-id="element-6220">
                    Заказы не найдены
                  </td>
                </tr> : currentOrders.map(o => <tr key={o.id} className={`transition-colors group ${selectedIds.has(o.id) ? 'bg-indigo-50/30' : o.isOverdue ? 'bg-rose-50/40 hover:bg-rose-50/70' : o.status === 'completed' ? 'bg-slate-50/50 hover:bg-slate-50' : 'hover:bg-slate-50'}`} data-id="element-6221">
                    <td className="px-2 py-2.5" data-id="element-6222">
                      <input type="checkbox" className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" checked={selectedIds.has(o.id)} onChange={() => toggleOne(o.id)} data-id="element-6223" />
                    </td>
                    {/* ID + Risk indicators */}
                    <td className="px-2 py-2.5" data-id="element-6224">
                      <div className="text-[11px] font-bold text-indigo-600 cursor-pointer hover:underline flex items-center gap-0.5" onClick={() => {
                  setSelectedOrder(o);
                  setActiveOrderTab('main');
                }} data-id="element-6225">
                        {o.id}
                        {o.isAI && <Zap className="w-2.5 h-2.5 text-violet-500" data-id="element-6226" />}
                      </div>
                      <div className="flex gap-1 mt-0.5" data-id="element-6227">
                        {o.isOverdue && <Tooltip text="⏱ Просрочен — срок выполнения истёк" data-id="element-6228">
                            <Clock className="w-3 h-3 text-rose-500 animate-pulse cursor-help" data-id="element-6229" />
                          </Tooltip>}
                        {o.hasDispute && <Tooltip text="⚠ Спор — клиент открыл диспут" data-id="element-6230">
                            <AlertTriangle className="w-3 h-3 text-orange-500 cursor-help" data-id="element-6231" />
                          </Tooltip>}
                        {o.hasActiveChat && <Tooltip text="💬 Активный чат — новые сообщения" data-id="element-6232">
                            <MessageSquare className="w-3 h-3 text-blue-500 cursor-help" data-id="element-6233" />
                          </Tooltip>}
                        {o.hasPaymentIssue && <Tooltip text="💳 Проблема оплаты" data-id="element-6234">
                            <CreditCard className="w-3 h-3 text-rose-500 cursor-help" data-id="element-6235" />
                          </Tooltip>}
                      </div>
                    </td>
                    {/* Type / Service */}
                    <td className="px-2 py-2.5" data-id="element-6236">
                      <div className="flex items-center gap-1 mb-0.5" data-id="element-6237">
                        {o.isAI ? <Badge variant="pro" size="sm" className="text-[8px] px-1 py-0 leading-tight" data-id="element-6238">
                            <Zap className="w-2 h-2 mr-0.5" data-id="element-6239" /> AI
                          </Badge> : o.orderType === 'Консультация' ? <Badge variant="success" size="sm" className="text-[8px] px-1 py-0 leading-tight" data-id="element-6240">
                            <Video className="w-2 h-2 mr-0.5" data-id="element-6241" /> Конс.
                          </Badge> : o.orderType === 'Курс' ? <Badge variant="featured" size="sm" className="text-[8px] px-1 py-0 leading-tight" data-id="element-6242">
                            Курс
                          </Badge> : <Badge variant="neutral" size="sm" className="text-[8px] px-1 py-0 leading-tight" data-id="element-6243">
                            Услуга
                          </Badge>}
                      </div>
                      <div className="text-[11px] text-slate-800 font-medium truncate max-w-[120px]" title={o.service} data-id="element-6244">
                        {o.service}
                      </div>
                    </td>
                    {/* Client */}
                    <td className="px-2 py-2.5" data-id="element-6245">
                      <div className="text-xs font-medium text-slate-900 cursor-pointer hover:text-indigo-600 truncate max-w-[100px]" title={o.client} onClick={() => {
                  const match = users.find(u => u.name === o.client);
                  if (match) setDetailPanel({
                    type: 'user_full',
                    title: 'Профиль клиента',
                    data: match
                  });
                }} data-id="element-6246">
                        {o.client}
                      </div>
                    </td>
                    {/* Astrologer + Status */}
                    <td className="px-2 py-2.5" data-id="element-6247">
                      {o.astrologer ? <>
                          <div className="text-xs text-slate-900 cursor-pointer hover:text-indigo-600 font-medium truncate max-w-[100px]" title={o.astrologer} onClick={() => {
                    const match = astrologers.find(a => a.name === o.astrologer);
                    if (match) setDetailPanel({
                      type: 'astrologer_profile',
                      title: `Профиль: ${match.name}`,
                      data: match
                    });
                    setActiveTab('astrologers');
                  }} data-id="element-6248">
                            {o.astrologer}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5" data-id="element-6249">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${o.astrologerStatus === 'отправил результат' ? 'bg-emerald-500' : o.astrologerStatus === 'выполняет' ? 'bg-amber-500' : o.astrologerStatus === 'принял заказ' ? 'bg-blue-500' : 'bg-slate-400'}`} data-id="element-6250" />
                            <span className="text-[9px] text-slate-500 truncate" data-id="element-6251">
                              {o.astrologerStatus}
                            </span>
                          </div>
                        </> : <span className="text-[10px] text-slate-400" data-id="element-6252">—</span>}
                    </td>
                    {/* Amount + Commission */}
                    <td className="px-2 py-2.5" data-id="element-6253">
                      <div className="text-xs font-bold text-slate-900" data-id="element-6254">
                        {o.amountNum.toLocaleString('ru-RU')} ₽
                      </div>
                      <div className="text-[9px] text-emerald-600 font-medium mt-0.5" data-id="element-6255">
                        {o.commission.toLocaleString('ru-RU')} ₽ (
                        {o.commissionRate * 100}%)
                      </div>
                    </td>
                    {/* Deadline + Duration COMBINED */}
                    <td className="px-2 py-2.5" data-id="element-6256">
                      <div className={`text-[11px] font-medium ${o.isOverdue ? 'text-rose-600 font-bold' : o.dueDate === 'Через 6 часов' ? 'text-amber-600' : 'text-slate-600'}`} data-id="element-6257">
                        {o.dueDate}
                      </div>
                      <div className="text-[9px] text-slate-400 flex items-center gap-0.5 mt-0.5" data-id="element-6258">
                        <Timer className="w-2.5 h-2.5" data-id="element-6259" />
                        {o.duration}
                      </div>
                    </td>
                    {/* Last Action */}
                    <td className="px-2 py-2.5" data-id="element-6260">
                      <div className="text-[11px] text-slate-700 truncate max-w-[110px]" title={o.lastAction} data-id="element-6261">
                        {o.lastAction}
                      </div>
                      <div className="text-[9px] text-slate-400 mt-0.5" data-id="element-6262">
                        {o.lastActionTime}
                      </div>
                    </td>
                    {/* Status */}
                    <td className="px-2 py-2.5" data-id="element-6263">
                      <Badge variant={o.status === 'completed' ? 'success' : o.status === 'disputed' ? 'error' : o.status === 'in_progress' ? 'warning' : 'neutral'} size="sm" className="text-[9px]" data-id="element-6264">
                        {o.status === 'completed' ? 'Завершён' : o.status === 'disputed' ? '⚠ Спор' : o.status === 'in_progress' ? 'В работе' : 'Отменён'}
                      </Badge>
                    </td>
                    {/* Actions */}
                    <td className="px-2 py-2.5 text-right relative" data-id="element-6265">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" icon={<MoreVertical className="w-3.5 h-3.5" data-id="element-6267" />} onClick={e => {
                  e.stopPropagation();
                  setOpenActionMenuId(openActionMenuId === o.id ? null : o.id);
                }} data-id="element-6266" />
                      {openActionMenuId === o.id && <div className="absolute right-0 top-9 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-[9999] animate-in fade-in zoom-in-95" data-id="element-6268">
                          <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    setSelectedOrder(o);
                    setActiveOrderTab('main');
                    setOpenActionMenuId(null);
                  }} data-id="element-6269">
                            <Eye className="w-3.5 h-3.5" data-id="element-6270" /> Открыть заказ
                          </button>
                          <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    setSelectedOrder(o);
                    setActiveOrderTab('chat');
                    setOpenActionMenuId(null);
                  }} data-id="element-6271">
                            <MessageSquare className="w-3.5 h-3.5" data-id="element-6272" /> Открыть
                            чат
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-6273" />
                          <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    const match = users.find(u => u.name === o.client);
                    if (match) setDetailPanel({
                      type: 'user_full',
                      title: 'Профиль клиента',
                      data: match
                    });
                    setOpenActionMenuId(null);
                  }} data-id="element-6274">
                            <User className="w-3.5 h-3.5" data-id="element-6275" /> Профиль клиента
                          </button>
                          {o.astrologer && <button className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                    const match = astrologers.find(a => a.name === o.astrologer);
                    if (match) setDetailPanel({
                      type: 'astrologer_profile',
                      title: `Профиль: ${match.name}`,
                      data: match
                    });
                    setActiveTab('astrologers');
                    setOpenActionMenuId(null);
                  }} data-id="element-6276">
                              <User className="w-3.5 h-3.5" data-id="element-6277" /> Профиль астролога
                            </button>}
                          <div className="h-px bg-slate-100 my-1" data-id="element-6278" />
                          <button className="w-full text-left px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => {
                    setShowCancelConfirm(o.id);
                    setOpenActionMenuId(null);
                  }} data-id="element-6279">
                            <Ban className="w-3.5 h-3.5" data-id="element-6280" /> Отменить заказ
                          </button>
                          <button className="w-full text-left px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => {
                    setShowRefundConfirm(o.id);
                    setOpenActionMenuId(null);
                  }} data-id="element-6281">
                            <Undo2 className="w-3.5 h-3.5" data-id="element-6282" /> Вернуть деньги
                          </button>
                        </div>}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-6283">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-6284" />
          </div>}
      </Card>

      {/* Floating Bulk Actions Bar */}
      {selectedIds.size > 0 && <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4 z-40 animate-in slide-in-from-bottom-10 fade-in" data-id="element-6285">
          <span className="font-medium text-sm" data-id="element-6286">
            {selectedIds.size} выбрано
          </span>
          <div className="w-px h-6 bg-slate-700" data-id="element-6287" />
          <Button size="sm" variant="primary" onClick={handleExport} icon={<Download className="w-4 h-4" data-id="element-6289" />} data-id="element-6288">
            Экспорт
          </Button>
          <Button size="sm" variant="outline" className="text-white border-slate-600 hover:bg-slate-800" onClick={handleBulkCancel} data-id="element-6290">
            Отменить
          </Button>
          <Button size="sm" variant="outline" className="text-rose-400 border-rose-800 hover:bg-rose-950 hover:text-rose-300" onClick={handleBulkRefund} data-id="element-6291">
            Вернуть деньги
          </Button>
        </div>}

      {/* Order Detail Modal */}
      {selectedOrder && <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in" onClick={e => {
      if (e.target === e.currentTarget) setSelectedOrder(null);
    }} data-id="element-6292">
          <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" padding="none" data-id="element-6293">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-white" data-id="element-6294">
              <div className="flex items-center gap-3" data-id="element-6295">
                <h3 className="text-lg font-bold text-slate-900" data-id="element-6296">
                  Заказ {selectedOrder.id}
                </h3>
                {selectedOrder.isAI && <Badge variant="pro" size="sm" data-id="element-6297">
                    <Zap className="w-3 h-3 mr-1" data-id="element-6298" /> AI
                  </Badge>}
                <Badge variant={selectedOrder.status === 'completed' ? 'success' : selectedOrder.status === 'disputed' ? 'error' : 'warning'} data-id="element-6299">
                  {selectedOrder.status === 'completed' ? 'Завершён' : selectedOrder.status === 'disputed' ? '⚠ Спор' : 'В работе'}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-full" onClick={() => setSelectedOrder(null)} data-id="element-6300">
                <X className="w-5 h-5" data-id="element-6301" />
              </Button>
            </div>

            {/* Modal Tabs */}
            <div className="px-4 border-b border-slate-200 bg-slate-50/50" data-id="element-6302">
              <div className="flex gap-6" data-id="element-6303">
                {[{
              id: 'main',
              label: 'Основное'
            }, {
              id: 'chat',
              label: 'Чат'
            }, {
              id: 'content',
              label: selectedOrder.isAI ? 'Отчёт' : 'Консультация / Файл'
            }, {
              id: 'payment',
              label: 'Платеж'
            }, {
              id: 'history',
              label: 'История'
            }].map(tab => <button key={tab.id} className={`py-3 text-sm font-medium border-b-2 transition-colors ${activeOrderTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveOrderTab(tab.id)} data-id="element-6304">
                    {tab.label}
                  </button>)}
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50" data-id="element-6305">
              {activeOrderTab === 'main' && <div className="space-y-6" data-id="element-6306">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-6307">
                    <Card padding="sm" className="border-slate-200" data-id="element-6308">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6309">Клиент</div>
                      <div className="font-medium text-slate-900" data-id="element-6310">
                        {selectedOrder.client}
                      </div>
                    </Card>
                    <Card padding="sm" className="border-slate-200" data-id="element-6311">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6312">
                        Астролог
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-6313">
                        {selectedOrder.astrologer || '—'}
                      </div>
                      {selectedOrder.astrologer && <div className="flex items-center gap-1 mt-1" data-id="element-6314">
                          <span className={`w-1.5 h-1.5 rounded-full ${selectedOrder.astrologerStatus === 'отправил результат' ? 'bg-emerald-500' : selectedOrder.astrologerStatus === 'выполняет' ? 'bg-amber-500' : selectedOrder.astrologerStatus === 'принял заказ' ? 'bg-blue-500' : 'bg-slate-400'}`} data-id="element-6315" />
                          <span className="text-[10px] text-slate-500" data-id="element-6316">
                            {selectedOrder.astrologerStatus}
                          </span>
                        </div>}
                    </Card>
                    <Card padding="sm" className="border-slate-200" data-id="element-6317">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6318">
                        Тип услуги
                      </div>
                      <div className="font-medium text-slate-900 flex items-center gap-2" data-id="element-6319">
                        {selectedOrder.isAI && <Zap className="w-4 h-4 text-indigo-500" data-id="element-6320" />}
                        {selectedOrder.service}
                      </div>
                    </Card>
                    <Card padding="sm" className="border-slate-200" data-id="element-6321">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6322">
                        Срок выполнения
                      </div>
                      <div className={`font-medium ${selectedOrder.isOverdue ? 'text-rose-600' : 'text-slate-900'}`} data-id="element-6323">
                        {selectedOrder.dueDate}
                      </div>
                    </Card>
                    <Card padding="sm" className="border-slate-200" data-id="element-6324">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6325">
                        Длительность
                      </div>
                      <div className="font-medium text-slate-900 flex items-center gap-1" data-id="element-6326">
                        <Timer className="w-3.5 h-3.5 text-slate-400" data-id="element-6327" />
                        {selectedOrder.duration}
                      </div>
                    </Card>
                    <Card padding="sm" className="border-slate-200" data-id="element-6328">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6329">
                        Последнее действие
                      </div>
                      <div className="font-medium text-slate-900 text-xs" data-id="element-6330">
                        {selectedOrder.lastAction}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5" data-id="element-6331">
                        {selectedOrder.lastActionTime}
                      </div>
                    </Card>
                  </div>

                  {selectedOrder.hasDispute && <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-id="element-6332">
                      <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0" data-id="element-6333" />
                      <div className="flex-1" data-id="element-6334">
                        <h4 className="text-sm font-bold text-rose-900" data-id="element-6335">
                          Открыт спор
                        </h4>
                        <p className="text-xs text-rose-700 mt-1" data-id="element-6336">
                          Клиент недоволен качеством консультации и требует
                          возврат средств. Ожидается решение модератора.
                        </p>
                        <div className="flex gap-2 mt-3" data-id="element-6337">
                          <Button size="sm" variant="primary" className="bg-rose-600 hover:bg-rose-700" onClick={() => success('Спор принят к рассмотрению')} data-id="element-6338">
                            Рассмотреть спор
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => success(`Возврат по заказу ${selectedOrder.id} оформлен`)} data-id="element-6339">
                            Вернуть деньги
                          </Button>
                        </div>
                      </div>
                    </div>}
                </div>}

              {activeOrderTab === 'chat' && <div className="space-y-4" data-id="element-6340">
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-6341">
                    <div className="p-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between" data-id="element-6342">
                      <div className="text-xs font-bold text-slate-700" data-id="element-6343">
                        Переписка по заказу {selectedOrder.id}
                      </div>
                      <Badge variant="neutral" size="sm" data-id="element-6344">
                        {selectedOrder.chatMessages.length} сообщений
                      </Badge>
                    </div>
                    <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto" data-id="element-6345">
                      {selectedOrder.chatMessages.map((msg: any, idx: number) => <div key={idx} className={`flex ${msg.from === 'client' ? 'justify-start' : 'justify-end'}`} data-id="element-6346">
                            <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${msg.from === 'client' ? 'bg-slate-100 text-slate-900 rounded-bl-md' : 'bg-indigo-600 text-white rounded-br-md'}`} data-id="element-6347">
                              <div className={`text-[10px] font-bold mb-1 ${msg.from === 'client' ? 'text-slate-500' : 'text-indigo-200'}`} data-id="element-6348">
                                {msg.name} · {msg.time}
                              </div>
                              <div className="text-sm" data-id="element-6349">{msg.text}</div>
                            </div>
                          </div>)}
                    </div>
                  </div>
                </div>}

              {activeOrderTab === 'content' && <div className="space-y-4" data-id="element-6350">
                  <h4 className="font-semibold text-slate-900 text-sm" data-id="element-6351">
                    Результат работы
                  </h4>
                  <Card padding="sm" className="border-slate-200 flex items-center justify-between" data-id="element-6352">
                    <div className="flex items-center gap-3" data-id="element-6353">
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-6354">
                        <FileText className="w-5 h-5 text-indigo-600" data-id="element-6355" />
                      </div>
                      <div data-id="element-6356">
                        <div className="text-sm font-medium text-slate-900" data-id="element-6357">
                          Отчет_Натальная_Карта.pdf
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-6358">
                          Загружен 14 Окт 2024 · 2.4 MB
                        </div>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-6360" />} data-id="element-6359">
                      Скачать
                    </Button>
                  </Card>
                  {!selectedOrder.isAI && <Card padding="sm" className="border-slate-200 flex items-center justify-between" data-id="element-6361">
                      <div className="flex items-center gap-3" data-id="element-6362">
                        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center" data-id="element-6363">
                          <Video className="w-5 h-5 text-purple-600" data-id="element-6364" />
                        </div>
                        <div data-id="element-6365">
                          <div className="text-sm font-medium text-slate-900" data-id="element-6366">
                            Запись консультации
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-6367">
                            Длительность: 45 мин · 128 MB
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" icon={<Eye className="w-4 h-4" data-id="element-6369" />} data-id="element-6368">
                        Смотреть
                      </Button>
                    </Card>}
                </div>}

              {activeOrderTab === 'payment' && <div className="space-y-4" data-id="element-6370">
                  <div className="grid grid-cols-3 gap-4" data-id="element-6371">
                    <Card padding="sm" className="border-slate-200 bg-white" data-id="element-6372">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6373">
                        Сумма оплаты
                      </div>
                      <div className="text-lg font-bold text-slate-900" data-id="element-6374">
                        {selectedOrder.amountNum.toLocaleString('ru-RU')} ₽
                      </div>
                    </Card>
                    <Card padding="sm" className="border-emerald-100 bg-emerald-50" data-id="element-6375">
                      <div className="text-xs text-emerald-700 mb-1" data-id="element-6376">
                        Комиссия платформы
                      </div>
                      <div className="text-lg font-bold text-emerald-700" data-id="element-6377">
                        {selectedOrder.commission.toLocaleString('ru-RU')} ₽
                      </div>
                      <div className="text-[10px] text-emerald-600" data-id="element-6378">
                        {selectedOrder.commissionRate * 100}% от суммы
                      </div>
                    </Card>
                    <Card padding="sm" className="border-slate-200 bg-white" data-id="element-6379">
                      <div className="text-xs text-slate-500 mb-1" data-id="element-6380">
                        Выплата астрологу
                      </div>
                      <div className="text-lg font-bold text-slate-900" data-id="element-6381">
                        {(selectedOrder.amountNum - selectedOrder.commission).toLocaleString('ru-RU')}{' '}
                        ₽
                      </div>
                    </Card>
                  </div>
                  <Card padding="sm" className="border-slate-200" data-id="element-6382">
                    <div className="flex justify-between items-center" data-id="element-6383">
                      <div data-id="element-6384">
                        <div className="text-sm font-medium text-slate-900" data-id="element-6385">
                          Статус транзакции
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5" data-id="element-6386">
                          Банковская карта **** 4242
                        </div>
                      </div>
                      <Badge variant={selectedOrder.paymentStatus === 'paid' ? 'success' : 'error'} data-id="element-6387">
                        {selectedOrder.paymentStatus === 'paid' ? 'Успешно' : 'Ошибка'}
                      </Badge>
                    </div>
                  </Card>
                </div>}

              {activeOrderTab === 'history' && <div className="space-y-0 relative pl-4" data-id="element-6388">
                  <div className="absolute left-[23px] top-4 bottom-4 w-px bg-slate-200" data-id="element-6389" />
                  {selectedOrder.history.map((h: any, idx: number) => <div key={idx} className="flex gap-4 pb-6 relative" data-id="element-6390">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1 relative z-10 border-2 ${h.text.includes('спор') ? 'bg-rose-100 border-rose-500' : h.text.includes('Завершён') || h.text.includes('отправлен') ? 'bg-emerald-100 border-emerald-500' : 'bg-indigo-100 border-indigo-500'}`} data-id="element-6391" />
                      <div data-id="element-6392">
                        <div className="text-sm font-medium text-slate-900" data-id="element-6393">
                          {h.text}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5" data-id="element-6394">
                          {h.time} · {h.actor}
                        </div>
                      </div>
                    </div>)}
                </div>}
            </div>
          </Card>
        </div>}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-6395">
          <Card className="w-full max-w-sm shadow-2xl text-center" data-id="element-6396">
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-4" data-id="element-6397">
              <Ban className="w-6 h-6" data-id="element-6398" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6399">
              Отменить заказ?
            </h3>
            <p className="text-sm text-slate-500 mb-6" data-id="element-6400">
              Вы уверены, что хотите отменить заказ {showCancelConfirm}? Статус
              будет изменён на «Отменён».
            </p>
            <div className="flex justify-center gap-3" data-id="element-6401">
              <Button variant="outline" onClick={() => setShowCancelConfirm(null)} data-id="element-6402">
                Нет, оставить
              </Button>
              <Button variant="primary" className="bg-amber-600 hover:bg-amber-700" onClick={() => cancelOrder(showCancelConfirm)} data-id="element-6403">
                Да, отменить
              </Button>
            </div>
          </Card>
        </div>}

      {/* Refund Confirmation Modal */}
      {showRefundConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" data-id="element-6404">
          <Card className="w-full max-w-sm shadow-2xl text-center" data-id="element-6405">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4" data-id="element-6406">
              <Undo2 className="w-6 h-6" data-id="element-6407" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-6408">
              Оформить возврат?
            </h3>
            <p className="text-sm text-slate-500 mb-6" data-id="element-6409">
              Средства по заказу {showRefundConfirm} будут возвращены клиенту.
              Заказ будет отменён.
            </p>
            <div className="flex justify-center gap-3" data-id="element-6410">
              <Button variant="outline" onClick={() => setShowRefundConfirm(null)} data-id="element-6411">
                Отмена
              </Button>
              <Button variant="primary" className="bg-rose-600 hover:bg-rose-700" onClick={() => refundOrder(showRefundConfirm)} data-id="element-6412">
                Вернуть деньги
              </Button>
            </div>
          </Card>
        </div>}
    </div>;
}